import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  Users,
  Package,
  CalendarPlus,
  AlertTriangle,
  ArrowRight,
  Loader2,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StatCard({ label, value, sub, icon: Icon, tint, testId }) {
  return (
    <div
      data-testid={testId}
      className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm hover:shadow-md transition"
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-medium uppercase tracking-wider text-slate-500">
            {label}
          </p>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900">
            {value}
          </h2>
          {sub && <p className="mt-1 text-xs text-slate-500">{sub}</p>}
        </div>
        <div
          className={`grid h-12 w-12 place-items-center rounded-xl ${tint}`}
        >
          <Icon size={22} strokeWidth={2.2} />
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const userName = localStorage.getItem("userName") || "Admin";
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        const { data } = await axios.get(`${API}/reports/summary`);
        if (alive) setData(data);
      } catch (e) {
        console.error(e);
        if (alive) setError("Failed to load dashboard.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  if (loading) {
    return (
      <div className="grid h-[60vh] place-items-center">
        <div className="flex items-center gap-3 text-slate-500">
          <Loader2 className="animate-spin" size={22} />
          Loading analytics…
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-red-700">
        {error}
      </div>
    );
  }

  const s = data?.summary || {};

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900">
            Welcome back, {userName}
          </h1>
          <p className="mt-1.5 text-sm text-slate-500">
            Today’s snapshot across your optical business.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link
            to="/add-customer"
            className="rounded-lg bg-slate-950 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
            data-testid="dashboard-add-customer"
          >
            + New Customer
          </Link>
          <Link
            to="/bookings"
            className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-800 hover:bg-slate-50"
            data-testid="dashboard-new-booking"
          >
            + Booking
          </Link>
        </div>
      </div>

      {/* Top Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
        <StatCard
          testId="stat-bookings"
          label="New Bookings"
          value={s.new_bookings ?? 0}
          sub="Awaiting shift"
          icon={CalendarPlus}
          tint="bg-sky-50 text-sky-600"
        />
        <StatCard
          testId="stat-customers"
          label="Customers"
          value={s.total_customers ?? 0}
          sub="Total registered"
          icon={Users}
          tint="bg-blue-50 text-blue-600"
        />
        <StatCard
          testId="stat-stock"
          label="Stock Items"
          value={s.total_stock ?? 0}
          sub={`${s.out_of_stock ?? 0} out of stock`}
          icon={Package}
          tint="bg-emerald-50 text-emerald-600"
        />
      </div>

      {/* Recent Bookings */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CalendarPlus size={18} className="text-sky-600" />
            <h3 className="text-base font-semibold text-slate-900">Recent Bookings</h3>
          </div>
          <Link to="/bookings" className="text-xs font-medium text-blue-600 hover:text-blue-700 flex items-center gap-1">
            All <ArrowRight size={12} />
          </Link>
        </div>
        {(!data?.recent_bookings || data.recent_bookings.length === 0) ? (
          <p className="text-sm text-slate-400">No bookings yet.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="dashboard-bookings">
            {data.recent_bookings.map((b) => (
              <Link
                key={b.id}
                to="/bookings"
                className="rounded-xl border border-slate-100 bg-slate-50/60 p-3 hover:bg-slate-100 transition"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-900 truncate">{b.customer_name}</p>
                    <p className="text-xs text-slate-500">{b.phone}</p>
                    <p className="text-xs text-slate-500 mt-1">{b.branch} · {b.visit_for || "—"}</p>
                  </div>
                  <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
                    b.status === "Converted" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                    b.status === "Cancelled" ? "bg-red-50 text-red-700 border-red-200" :
                    "bg-blue-50 text-blue-700 border-blue-200"
                  }`}>
                    {b.status}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Low Stock */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertTriangle size={18} className="text-amber-500" />
            <h3 className="text-base font-semibold text-slate-900">Low Stock</h3>
          </div>
          <Link to="/stock" className="text-xs font-medium text-blue-600 hover:text-blue-700 flex items-center gap-1">
            Manage <ArrowRight size={12} />
          </Link>
        </div>
        {(!data?.low_stock || data.low_stock.length === 0) ? (
          <p className="text-sm text-slate-400">All items well stocked.</p>
        ) : (
          <ul className="space-y-3">
            {data.low_stock.map((s) => (
              <li
                key={s.id}
                className="flex items-center justify-between rounded-lg border border-slate-100 px-3 py-2.5"
              >
                <div>
                  <p className="text-sm font-semibold text-slate-800">{s.product_name}</p>
                  <p className="text-xs text-slate-500">{s.category} · {s.brand || "—"}</p>
                </div>
                <span className={`text-xs font-bold ${
                  s.quantity === 0 ? "text-red-600" : "text-amber-600"
                }`}>
                  {s.quantity} left
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Quick nav */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {[
          { to: "/bookings", label: "Bookings", icon: CalendarPlus, tint: "text-sky-600 bg-sky-50" },
          { to: "/customer", label: "Customers", icon: Users, tint: "text-blue-600 bg-blue-50" },
          { to: "/stock", label: "Stock", icon: Package, tint: "text-emerald-600 bg-emerald-50" },
        ].map(({ to, label, icon: Icon, tint }) => (
          <Link
            key={to}
            to={to}
            className="group rounded-xl border border-slate-200 bg-white p-4 shadow-sm hover:shadow-md transition flex items-center gap-3"
          >
            <div className={`grid h-10 w-10 place-items-center rounded-lg ${tint}`}>
              <Icon size={18} />
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-800 group-hover:text-blue-600">
                {label}
              </p>
              <p className="text-xs text-slate-500">Open</p>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
